import React, { useCallback, useEffect } from 'react'
import qs from 'query-string'
import classNames from 'classnames'
import ReactModal from 'react-modal'
import ReactSVG from 'react-svg'
import { pathEq, pathOr } from 'ramda'
import {
  matchPath, useHistory, useLocation
} from 'react-router-dom'
import { ThemeProvider } from 'react-jss'

import {
  MODALS,
  STATIC_SCREENS,
  THEME_OPTIONS,
  AUTH_REQUIRED_MODALS
} from '../../constants'
import {
  getModalComponent,
  getModalTheme,
  MODALS_NOT_SHOW_AGAIN_NAVIGATE_BACK,
  MODALS_DO_NOT_CAUSE_SCREEN_RELOAD,
  MODALS_WITH_GRID_IMAGE_BACKGROUND,
  MODALS_HIDE_CLOSE_BUTTON,
  MODALS_SPECIAL_SIZE
} from './routed-modal-registry'
import themeStyles from '../../theme'
import { useConfig } from '../../hooks/use-config'
import { useIsAuthenticated } from '../../hooks/use-is-authenticated'
import { getConfigValue } from '../../utils/getConfigValue'
import { getModalLocation } from '../../lib/modal'
import CloseIcon from '../../../images/closeModal.svg'
import styles from './modal.css'

const getModalFromPath = location => {
  const parsedLocation = matchPath(location.pathname, {
    path: '/movie/:movieSlug/:modal?'
  })

  if (pathEq(['params', 'modal'], 'rent', parsedLocation)) {
    return {
      modal: MODALS.qsParams.loginRental,
      modalCloseTarget: `/movie/${parsedLocation.params.movieSlug}`
    }
  }

  return {
    modal: null,
    modalCloseTarget: location.pathname
  }
}

const getModalFromSearch = location => {
  // Modal component will look for a ?modal=name query string
  // parameter in react-router location object and will render
  const parsedSearch = qs.parse(location && location.search)

  return {
    modal: pathOr(null, ['modal'], parsedSearch),
    modalCloseTarget: location.pathname
  }
}

// Rental paths take precedence over the modal query parameter.
const getModalParameter = location => {
  const modalParameter = getModalFromPath(location)

  if (modalParameter.modal) {
    return modalParameter
  }

  return getModalFromSearch(location)
}

/**
 * Displays the modal selected by the URL and manages authentication and close navigation.
 * Supplies routing, modal configuration, and close handlers to the selected modal component.
 */
function RoutedModal(props) {
  const history = useHistory()
  const location = useLocation()
  const isAuthenticated = useIsAuthenticated()
  const { appConfig } = useConfig()

  const modalParameter = getModalParameter(location)
  const { modal } = modalParameter
  let { modalCloseTarget } = modalParameter
  let modalCloseTargetSearch = ''
  const requiresLogin = AUTH_REQUIRED_MODALS.includes(modal) && !isAuthenticated

  // Redirect to login modal if modal required user login
  useEffect(() => {
    if (requiresLogin) {
      history.push(getModalLocation(location, MODALS.qsParams.login))
    }
  }, [history, location, requiresLogin])

  // My Account and Notifications redirect logged-out users to the login modal.
  // Closing it would send them Home instead.
  // Logging in still keeps the user on the current screen.
  if (modal === MODALS.qsParams.login && (
    location.pathname.includes('my-account') ||
    location.pathname === STATIC_SCREENS.NOTIFICATIONS.screenId
  )) {
    modalCloseTarget = '/'
  }

  // Preserve the existing signup query parameters when a plan is selected.
  if (location.pathname.includes('sign-up')) {
    const search = qs.parse(location.search)
    const { modal: loginModal, ...otherParams } = search
    if (search.plan) {
      modalCloseTargetSearch = qs.stringify(otherParams)
    }
  }

  const doNotShowAgainWhenNavigateBack = MODALS_NOT_SHOW_AGAIN_NAVIGATE_BACK.includes(modal)

  const onRequestClose = useCallback(() => {
    if (doNotShowAgainWhenNavigateBack) {
      // Clear the modal query from this history entry before returning to the previous one.
      history.replace({
        ...location,
        search: null
      })
      history.goBack()
    } else {
      history.push({
        pathname: modalCloseTarget,
        search: modalCloseTargetSearch
      })
    }
  }, [
    doNotShowAgainWhenNavigateBack,
    history,
    location,
    modalCloseTarget,
    modalCloseTargetSearch
  ])

  // Do not mount protected modal content while the login redirect is pending.
  if (requiresLogin) return null

  const modalProps = {
    ...props,
    history,
    location,
    isAuthenticated,
    modal,
    modalCloseTarget,
    modalCloseTargetSearch,
    isOpen: Boolean(modal),
    className: {
      base: styles.modal,
      afterOpen: styles.modalAfterOpen,
      beforeClose: styles.modalBeforeClose
    },
    overlayClassName: {
      base: styles.overlay,
      afterOpen: styles.overlayAfterOpen,
      beforeClose: styles.overlayBeforeClose
    },
    shouldCloseOnOverlayClick: false,
    bodyOpenClassName: MODALS_DO_NOT_CAUSE_SCREEN_RELOAD.includes(modal)
      ? 'ReactModal__Body--open-preserve-scroll'
      : 'ReactModal__Body--open',
    doNotShowAgainWhenNavigateBack,
    onRequestClose
  }

  const Component = modal && getModalComponent(modal)
  const theme = getModalTheme(modal)
  const isFlexibleSize = MODALS_SPECIAL_SIZE.FLEXIBLE.includes(modal)
  const isLargeSize = MODALS_SPECIAL_SIZE.LARGE.includes(modal)
  const isFullScreenSize = MODALS_SPECIAL_SIZE.FULL_SCREEN.includes(modal)

  const applyTheme = (...classes) => classNames(...classes, {
    [styles.darkTheme]: theme === THEME_OPTIONS.dark
  })

  // Apply background image for some modals.
  const gridImageBackgroundStyle = {}
  const guestHotspotImage = getConfigValue(appConfig?.values, 'GUEST_HOME_HOTSPOT_IMAGE')
  const hasGridImageBackground = MODALS_WITH_GRID_IMAGE_BACKGROUND.includes(modal)
  if (hasGridImageBackground && guestHotspotImage) {
    gridImageBackgroundStyle.backgroundImage = `
      linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)),
      url(${guestHotspotImage})
    `
    gridImageBackgroundStyle.backgroundSize = 'cover'
    gridImageBackgroundStyle.backgroundPosition = 'center'
  }

  const isHideCloseButton = MODALS_HIDE_CLOSE_BUTTON.includes(modal)

  return (
    <ReactModal contentLabel="lbx_modal" {...modalProps}>
      <div
        style={gridImageBackgroundStyle}
        className={applyTheme(
          styles.bodyWrapper,
          isFlexibleSize && styles.flexibleSizeModal,
          isLargeSize && styles.largeSizeModal,
          isFullScreenSize && styles.fullScreenModal
        )}
      >
        <div className={styles.headline}>
          <button
            type="button"
            data-lbx-e2e="modal-close-button"
            className={applyTheme(styles.modalCloseIcon, isHideCloseButton && styles.hide)}
            onClick={onRequestClose}
          >
            <ReactSVG
              src={CloseIcon}
              wrapper="span"
              beforeInjection={svg => {
                svg.setAttribute('class', applyTheme(styles.closeIcon))
              }}
            />
          </button>
        </div>
        <div
          className={applyTheme(styles.modalBody, hasGridImageBackground && styles.narrowModalBody)}
        >
          <ThemeProvider theme={themeStyles}>
            {Component && React.isValidElement(<Component />) ? (
              <Component {...modalProps} />
            ) : (
              `${modal} is not defined in modals array.`
            )}
          </ThemeProvider>
        </div>
      </div>
    </ReactModal>
  )
}

export default RoutedModal
